import {Component, DoCheck, Input, IterableDiffer, IterableDiffers, OnInit, Output, QueryList, ViewChildren} from '@angular/core';
import {Observable, of, ReplaySubject} from "rxjs";
import {Router} from "@angular/router";
import {MatExpansionPanel} from "@angular/material/expansion";
import {HttpParams} from "@angular/common/http";
import {SessionService} from "../../session/session.service";
import {Usuario} from "../../model/personas/usuario.model";
import {Persona} from "../../model/personas/persona.model";
import {ApiInterface} from "../../api/api-interface";
import {MenuIconResolver} from "./menu-icon.resolver";
import {FloatingHelpService} from "../../floating-help/floating-help.service";
import {esRutaSoloEscritorio, ModoConsultaService} from "../../services/modo-consulta.service";

export const MENU_ENDPOINT = 'items';

/**
 * `link` reservado para la entrada de menú que abre el panel de ayuda en vez de navegar. Empieza
 * por '#', así que no puede chocar con ninguna ruta. La fila de `persona_item` no la crea ninguna
 * migración: es un paso manual (pasos-manuales/2026-09-28-menu-ayuda.sql).
 */
export const ENLACE_AYUDA = '#ayuda';

export interface MenuItem {
    id: number;
    name: string;
    link?: string | null;
    icono?: string | null;
    queryParams: string | null;
    requiredRole: string;
    orden?: string | null;
    children: MenuItem[];
}

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements DoCheck, OnInit {
    private static nextId = 0;
    private _id;

    @Input() public items: MenuItem[];
    /**
     * `true` en el menú anidado que se pinta dentro de un grupo desplegable.
     * Los submenús no muestran icono: el del grupo ya identifica la sección y
     * repetir iconos en el segundo nivel recarga la columna.
     */
    @Input() public submenu = false;
    @Output() public itemClicked$ = new ReplaySubject<void>();

    @ViewChildren(MatExpansionPanel) private _panels: QueryList<MatExpansionPanel>;

    private _expandableItems: MenuItem[];

    private _finalItems: MenuItem[];

    /** Móvil y tableta (modo consulta): la plantilla oculta las entradas que crean trabajos. */
    public readonly soloConsulta$: Observable<boolean>;
    private _iterableDiffer: IterableDiffer<MenuItem>;


    constructor(private _iterableDiffers: IterableDiffers, private _router: Router, private _sesion: SessionService,
                private _iconos: MenuIconResolver, private _ayuda: FloatingHelpService,
                private _consulta?: ModoConsultaService) {
        this.soloConsulta$ = this._consulta?.activo$ ?? of(false);
        this._expandableItems = [];
        this._finalItems = [];
        this.items = [];
        this._id = MenuComponent.nextId++;

    }

    ngOnInit(): void {

        this._iterableDiffer = this._iterableDiffers.find(this.items).create(MenuComponent.sTrackItemsBy);


    }

    static sTrackItemsBy(index: number, item: MenuItem) {

        return item.id;
    }

    ngDoCheck(): void {
        const changes = this._iterableDiffer.diff(this.items);
        if (changes) {
            let finalsChanged = false;
            changes.forEachRemovedItem(rec => {
                let idx = this._expandableItems.findIndex(it => it.id === rec.trackById);
                if (idx >= 0) {
                    this._expandableItems.splice(idx, 1);
                    return;
                }
                idx = this._finalItems.findIndex(it => it.id === rec.trackById);
                if (idx >= 0) {
                    this._finalItems.splice(idx, 1);
                    finalsChanged = true;
                }
            });
            changes.forEachAddedItem(rec => {
                this._addCounting(rec.item);
                finalsChanged = finalsChanged || rec.item.children.length === 0;
            });
        }
    }

    private _addCounting(item: MenuItem) {
        let index = 0;
        let comp = (next) => next.children.length === 0;
        if (item.children.length > 0) {
            comp = (next) => next.children.length > 0;
        }
        for (const next of this.items) {
            if (next.id === item.id) {
                break;
            }
            if (comp(next)) {
                index++;
            }
        }
        if (item.children.length > 0) {
            this._expandableItems.splice(index, 0, item);
        } else {
            this._finalItems.splice(index, 0, item);
        }
    }

    public get expandableItems(): MenuItem[] {
        return this._expandableItems;
    }

    public get finalItems(): MenuItem[] {
        return this._finalItems;
    }

    public queryParams(query: string | null) {
        if (query) {
            const params = new HttpParams({fromString: query});
            const obj = {};
            for (const key of params.keys()) {
                obj[key] = params.getAll(key);
            }
            return obj;
        }
        return undefined;
    }

    public trackByFn(index: number, item: MenuItem) {
        return MenuComponent.sTrackItemsBy(index, item);
    }

    /** Si la sección se pinta con un SVG propio (o el de respaldo) en vez de con la fuente Material. */
    public esSvg(icono: string | null | undefined): boolean {
        return this._iconos.iconoDeMenu(icono).kind === 'svg';
    }

    /** Nombre registrado del SVG, para el `[svgIcon]` de la plantilla. */
    public svgIcono(icono: string | null | undefined): string {
        const spec = this._iconos.iconoDeMenu(icono);
        return spec.kind === 'svg' ? spec.name : '';
    }

    /** Ligature de Material con la que se pinta la sección. */
    public ligatura(icono: string | null | undefined): string {
        const spec = this._iconos.iconoDeMenu(icono);
        return spec.kind === 'font' ? spec.ligature : '';
    }

    /** Si la entrada lleva a crear o rehacer un trabajo («Nuevo trabajo»): en modo consulta no se ofrece. */
    public esSoloEscritorio(item: MenuItem): boolean {
        return esRutaSoloEscritorio(item.link);
    }

    /** Si la entrada es la de ayuda, que abre el panel en vez de navegar. */
    public esAyuda(item: MenuItem): boolean {
        return item.link === ENLACE_AYUDA;
    }

    /**
     * Abre o cierra el panel de ayuda. Avisa del clic como cualquier otra entrada: en pantallas
     * pequeñas el menú tapa la página y hay que cerrarlo para ver el panel.
     */
    public abrirAyuda(): void {
        this._ayuda.toggle();
        this.itemClicked$.next();
    }
}
